/**
 * RegistrationFieldsPanel - renders tenant-scoped RegistrationField
 * instances that are assigned to the current wizard step via
 * `capture_step`.
 *
 * Fields are grouped into trigger + dependent units: a field that declares
 * `configuration_data.visible_when` (e.g. a product picker revealed by a
 * "was X used?" toggle) is nested beneath the most recent field that does
 * not. The grouping is driven purely by config, so it stays tenant-agnostic
 * while letting each toggle + its follow-up read as one decision. Visibility
 * itself is resolved upstream, so a dependent field only reaches this panel
 * when its trigger is active.
 *
 * Rendering still flows through the shared MetadataField renderer so custom
 * fields look identical to product-template attributes.
 */

import { useMemo } from 'react'
import { ClipboardList } from 'lucide-react'
import {
  MetadataField,
  normaliseRegistrationField,
  isConditionallyRequired,
  isRequiredValueProvided,
} from '@/shared/lib/forms'
import {
  useRequiredFieldErrors,
  type RequiredFieldSpec,
} from './wizard-validation'

/**
 * Minimal structural shape the panel renders. Any field source satisfies it:
 * a tenant-scoped `RegistrationField`, or a product-template EAV `FieldMeta`
 * (whose `field_type` union is wider, e.g. `ENTITY_SEARCH`). Kept deliberately
 * loose (`field_type: string`) so both feed the same render path unchanged.
 */
export interface RegistrationFieldsPanelField {
  uuid: string
  code_name: string
  display_name: string
  description?: string
  field_type: string
  required: boolean
  configuration_data?: Record<string, unknown>
  order?: number
}

interface RegistrationFieldsPanelProps {
  fields: RegistrationFieldsPanelField[]
  values: Record<string, unknown>
  onChange: (next: Record<string, unknown>) => void
  /**
   * Visual variant.
   * - `panel` (default): renders the fields inside a bordered panel with
   *   a header. Used as a standalone grid item.
   * - `inline`: renders just the grouped fields, no wrapper or header. Used
   *   when the fields are slotted inside another panel (e.g. the Property
   *   panel on the host_context step).
   */
  variant?: 'panel' | 'inline'
  /** Header label for the `panel` variant. */
  title?: string
}

interface FieldGroup {
  trigger: RegistrationFieldsPanelField
  dependents: RegistrationFieldsPanelField[]
}

function hasVisibleWhen(field: RegistrationFieldsPanelField): boolean {
  const cd = field.configuration_data as Record<string, unknown> | undefined
  return !!cd && !!cd.visible_when
}


export function RegistrationFieldsPanel({
  fields,
  values,
  onChange,
  variant = 'panel',
  title = 'Additional Details',
}: RegistrationFieldsPanelProps) {
  const specs = useMemo<RequiredFieldSpec[]>(
    () =>
      fields
        .filter(
          (f) =>
            f.required ||
            isConditionallyRequired(
              f.configuration_data as Record<string, unknown> | undefined,
              values,
            ),
        )
        .map((f) => ({
          key: f.code_name,
          label: f.display_name,
          // Type-aware: a required disclaimer / consent checkbox is only
          // satisfied when checked (`=== true`); the naive non-empty test
          // would let an unchecked box (value `false`) through.
          isFilled: isRequiredValueProvided(
            {
              field_type: f.field_type,
              configuration_data: f.configuration_data as
                | Record<string, unknown>
                | null
                | undefined,
            },
            values[f.code_name],
          ),
          message: `${f.display_name} is required`,
        })),
    [fields, values],
  )
  // Scope id is stable per panel instance to avoid stomping when the
  // shared panel renders twice on the same step (e.g. inline anchored
  // sub-panels + the main unanchored panel). Joining every field uuid
  // makes each surface uniquely registered with the wizard validation
  // context.
  const scopeId = useMemo(
    () =>
      `reg-fields:${variant}:${fields.map((f) => f.uuid).join('|') || 'empty'}`,
    [variant, fields],
  )
  const errors = useRequiredFieldErrors(scopeId, specs)

  // Fold the flat field list into trigger + dependent groups.
  const groups = useMemo<FieldGroup[]>(() => {
    const out: FieldGroup[] = []
    for (const field of fields) {
      if (hasVisibleWhen(field) && out.length > 0) {
        out[out.length - 1].dependents.push(field)
      } else {
        out.push({ trigger: field, dependents: [] })
      }
    }
    return out
  }, [fields])

  if (fields.length === 0) return null

  const renderField = (field: RegistrationFieldsPanelField) => (
    <MetadataField
      key={field.uuid}
      field={normaliseRegistrationField({
        uuid: field.uuid,
        code_name: field.code_name,
        display_name: field.display_name,
        field_type: field.field_type,
        required: field.required,
        description: field.description,
        configuration_data: field.configuration_data,
        order: field.order,
      })}
      value={values[field.code_name] ?? ''}
      onChange={(v) => onChange({ [field.code_name]: v })}
      labelVariant="compact"
      submissionMode="publish"
      error={errors[field.code_name]}
    />
  )

  const groupNodes = groups.map((group) => {
    // A field that is itself conditionally revealed (its `visible_when`
    // points at an upstream field, e.g. `other_structure_type` driven by the
    // host attribute `structure_type`) and has no follow-ups of its own is a
    // standalone revealed input. Render it flush.
    const isLoneRevealedField =
      group.dependents.length === 0 && hasVisibleWhen(group.trigger)

    if (isLoneRevealedField) {
      return <div key={group.trigger.uuid}>{renderField(group.trigger)}</div>
    }

    return (
      <div key={group.trigger.uuid} className="flex flex-col gap-2">
        {renderField(group.trigger)}
        {group.dependents.map(renderField)}
      </div>
    )
  })

  if (variant === 'inline') {
    return <div className="flex flex-col gap-2.5">{groupNodes}</div>
  }

  return (
    <div className="rounded-xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
      <div className="mb-3 flex items-center gap-2 border-b-[0.5px] border-b-[var(--rule)] pb-2.5">
        <ClipboardList size={14} className="text-[var(--ink-3)]" />
        <p className="m-0 text-[10px] font-light uppercase tracking-[0.16em] text-[var(--ink-3)]">
          {title}
        </p>
      </div>
      <div className="flex flex-col divide-y divide-[var(--rule)]">
        {groupNodes.map((node, i) => (
          <div key={i} className={i === 0 ? 'pb-3' : 'py-3'}>
            {node}
          </div>
        ))}
      </div>
    </div>
  )
}
